'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Session } from 'next-auth';
import { signOut } from 'next-auth/react';
import { Bot, Settings, LogOut } from 'lucide-react';

interface NosOutreachAuthenticatedNavigationProps {
	session: Session;
}

export function NosOutreachAuthenticatedNavigation({ session }: NosOutreachAuthenticatedNavigationProps) {
	const initials = session.user?.name
		? session.user.name.split(' ').map(n => n[0]).join('').toUpperCase()
		: session.user?.email?.[0]?.toUpperCase() || 'U';

	return (
		<nav className="flex items-center justify-between px-6 py-4 bg-white/80 backdrop-blur-sm border-b border-orange-200">
			<div className="flex items-center space-x-6">
				<Link href="/agents" className="flex items-center gap-2">
					<div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-md">
						<span className="text-white font-black text-xl tracking-wider">NOS</span>
					</div>
					<span className="text-xl font-bold text-gray-900">Outreach</span>
				</Link>
			</div>

			<div className="flex items-center space-x-4">
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" className="relative h-10 w-10 rounded-full">
							<Avatar className="h-10 w-10">
								<AvatarImage src={session.user?.image || ''} alt={session.user?.name || ''} />
								<AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-600 text-white">
									{initials}
								</AvatarFallback>
							</Avatar>
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent className="w-56" align="end" forceMount>
						<div className="flex flex-col space-y-1 p-2">
							<p className="text-sm font-medium leading-none">{session.user?.name}</p>
							<p className="text-xs leading-none text-muted-foreground">
								{session.user?.email}
							</p>
						</div>
						<DropdownMenuSeparator />
						<DropdownMenuItem asChild>
							<Link href="/settings" className="flex items-center">
								<Settings className="mr-2 h-4 w-4" />
								Settings
							</Link>
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className="text-red-600 focus:text-red-600"
							onClick={() => signOut({ callbackUrl: '/' })}
						>
							<LogOut className="mr-2 h-4 w-4" />
							Sign Out
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</nav>
	);
}